import { useQuery } from "@tanstack/react-query";
import { uniq } from "lodash-es";
import { useCallback, useMemo } from "react";

import type {
  FlightFare,
  FlightFareLeg,
} from "@/features/flights/components/flight-fares/types";
import { useFlightSelectionPayloadContext } from "@/features/flights/contexts/FlightSelectionPayloadContext/FlightSelectionPayloadContext";
import { useSelectedFlightOffer } from "@/features/flights/hooks/flightSearch/useSelectedFlightOffer";
import { flightUpsellQueryOptions } from "@/features/flights/hooks/useFlightUpsell";
import type { FlightOffer } from "@/features/flights/types/types";

const DEFAULT_FARE_CODE = "__BASE";

/**
 * Overview:
 * Departure and return fares are actually could be ONLY selected as combinations,
 * but for better UX we show them separately with some filtering rules:
 * - For departure fare selection: show all UNIQUE(cheapest) available fares with the default fare included
 * - For arrival fare selection: show all UNIQUE(cheapest) available fares that match the brand of the selected departure fare
 *    - If departure brand is default, show all arrival fares with the default brand included. This is because the only free and guaranteed available offer for default departure is the default arrival
 *
 * Hook handles everything related to fetching and preparing the data for the fare selection pages, as well as managing the selected fare in the context.
 */

type UseFlightFaresParams = {
  onFaresLoaded?: (
    availableFares: FlightFare[],
    defaultFare: FlightFare,
  ) => void;
};

export function useFlightFares({ onFaresLoaded }: UseFlightFaresParams = {}) {
  const { selectedFare, setSelectedFare } = useFlightSelectionPayloadContext();
  const { offer, arrivalFlight } = useSelectedFlightOffer();

  const {
    data,
    isLoading: isFaresLoading,
    isError: isFaresError,
  } = useQuery(
    flightUpsellQueryOptions({
      index: offer.index,
      body: {
        searchResultKey: offer.searchResultKey,
        searchResultToken: offer.searchResultToken,
        itineraryIndex: offer.itineraryIndex,
        currentOfferPrice: offer.totalPrice,
        currentBrandCodes: uniq(
          offer.defaultFare?.legs.map((leg) => leg.brandCode),
        ).filter((c) => c != null),
        currentCabins: uniq(
          offer.defaultFare?.legs.map((leg) => leg.cabin),
        ).filter((c) => c != null),
      },
      onSuccess: (data) => {
        onFaresLoaded?.(data.upsellOptions, createDefaultFare(offer));
      },
    }),
  );

  const { defaultFare, availableFares } = useMemo<{
    defaultFare: FlightFare;
    availableFares: FlightFare[];
  }>(() => {
    const defaultFare: FlightFare = createDefaultFare(offer);

    if (!data) {
      return {
        defaultFare,
        availableFares: [],
      };
    }

    return {
      defaultFare,
      availableFares: data.upsellOptions,
    };
  }, [data, offer]);

  const departureFares = useMemo(() => {
    return buildFareOptions({
      fares: availableFares,
      getLeg: getDepartureLegFromFare,
      defaultFare,
      includeDefault: true,
    });
  }, [availableFares, defaultFare]);

  const arrivalFares = useMemo(() => {
    if (!arrivalFlight) return [];

    const isDepartureFareDefault = selectedFare === null;
    const selectedDepartureFareBrandCode = getDepartureLegFromFare(
      selectedFare ?? defaultFare,
    )?.brandCode;

    return buildFareOptions({
      fares: availableFares,
      getLeg: getArrivalLegFromFare,
      defaultFare,
      preFilter: (fare) =>
        getDepartureLegFromFare(fare)?.brandCode ===
        selectedDepartureFareBrandCode,
      includeDefault: isDepartureFareDefault,
    });
  }, [arrivalFlight, availableFares, selectedFare, defaultFare]);

  const setSelectedFareWrapper = useCallback(
    (fare: FlightFare) => {
      if (fare.code === DEFAULT_FARE_CODE) {
        setSelectedFare(null);
        return;
      }

      setSelectedFare(fare);
    },
    [setSelectedFare],
  );

  return {
    defaultFare,
    selectedFare,
    setSelectedFare: setSelectedFareWrapper,
    isFaresLoading,
    isFaresError,
    departureFares,
    arrivalFares,
  };
}

function createDefaultFare(offer: FlightOffer): FlightFare {
  return {
    code: DEFAULT_FARE_CODE,
    totalPrice: offer.totalPrice,
    currency: offer.currency,
    legs: offer.defaultFare?.legs ?? [],
    priceDifference: 0,
  };
}

export function checkIsFareDefault(fare: FlightFare) {
  return fare.code === DEFAULT_FARE_CODE;
}

export function getDepartureLegFromFare(
  fare: FlightFare | Pick<FlightFare, "legs"> | null,
) {
  return fare?.legs[0] ?? null;
}

export function getArrivalLegFromFare(
  fare: FlightFare | Pick<FlightFare, "legs"> | null,
) {
  return fare?.legs[1] ?? null;
}

type BuildFareOptionsParams = {
  fares: FlightFare[];
  getLeg: (fare: FlightFare) => FlightFareLeg | null;
  defaultFare: FlightFare;
  preFilter?: (fare: FlightFare) => boolean;
  includeDefault: boolean;
};

// Constructs a list of fare options to display, ensuring only one fare per brand and optionally including the default fare.
// - Takes cheapest fare per brand
// - When includeDefault=true: skips the default brand in the map to avoid duplicates, since defaultFare is manually
//   prepended at the end. This also means the default fare is always shown as the constructed object, not an API option.
// - When includeDefault=false (arrival with non-default departure): does NOT skip the default brand, allowing
//   default-brand arrival legs to appear as regular (paid) upsell options in that combination.
function buildFareOptions({
  fares,
  getLeg,
  defaultFare,
  preFilter,
  includeDefault,
}: BuildFareOptionsParams): FlightFare[] {
  const defaultBrandCode = getLeg(defaultFare)?.brandCode;
  const fareCandidates = preFilter ? fares.filter(preFilter) : fares;

  const map: Record<string, FlightFare> = {};
  for (const fare of fareCandidates) {
    const brandCode = getLeg(fare)?.brandCode;

    if (!brandCode || (includeDefault && brandCode === defaultBrandCode))
      continue;

    if (!map[brandCode] || fare.totalPrice < map[brandCode].totalPrice) {
      map[brandCode] = fare;
    }
  }
  return [includeDefault ? defaultFare : null, ...Object.values(map)].filter(
    (fare) => fare !== null,
  );
}
